import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { refreshStoredAuthUser } from "@/lib/authUser";

export interface RoleRecord {
  id: string;
  name: string;
  guardName: string;
  /** Null = global (no tenant) role row. */
  companyId: string | null;
  companyName?: string | null;
  isTemplate: boolean;
  /** From API `withCount` when listing roles. */
  permissionsCount: number;
  createdAt: string;
  updatedAt: string;
}

export type RoleDraft = {
  name: string;
  /** Spatie permission names for `syncPermissions`. */
  permissionNames: string[];
};

const LS_KEY = "inventory.roles.v2";

const SYSTEM_ROLE_NAMES = new Set(["Admin", "HR", "Accountant", "Employee"]);

export function isSystemRoleName(name: string): boolean {
  return SYSTEM_ROLE_NAMES.has(name);
}

type RoleApiRecord = {
  id?: number | string;
  name: string;
  company_id?: number | null;
  company_name?: string | null;
  is_template?: boolean;
  guard_name?: string;
  permissions_count?: number;
  permissions?: string[] | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertRoleApiResponse = {
  message?: string;
  data?: RoleApiRecord;
};

type RoleShowApiResponse = {
  data?: RoleApiRecord;
};

type DeleteRoleApiResponse = {
  message?: string;
};

type UpsertRoleResult = {
  record: RoleRecord;
  message: string;
};

type DeleteRoleResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: RoleRecord[]): RoleRecord[] {
  const byId = new Map<ID, RoleRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function load(): RoleRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<RoleRecord>[];
    if (!Array.isArray(parsed)) return [];
    return dedupeById(
      parsed
        .filter((r) => r && r.id && r.name)
        .map((r) => ({
          id: String(r.id),
          name: String(r.name),
          guardName: r.guardName ?? "web",
          companyId: r.companyId != null && r.companyId !== "" ? String(r.companyId) : null,
          companyName: (r as Partial<RoleRecord>).companyName ?? null,
          isTemplate: Boolean((r as Partial<RoleRecord>).isTemplate),
          permissionsCount: typeof r.permissionsCount === "number" ? r.permissionsCount : 0,
          createdAt: r.createdAt ?? new Date().toISOString(),
          updatedAt: r.updatedAt ?? r.createdAt ?? new Date().toISOString(),
        })),
    );
  } catch {
    return [];
  }
}

function save(items: RoleRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): RoleApiRecord[] {
  if (Array.isArray(body)) return body as RoleApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as RoleApiRecord[];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const inner = (data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as RoleApiRecord[];
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractRoleFromUpsertBody(body: unknown): RoleApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as RoleApiRecord;
  }
  return null;
}

function mapApiRecord(record: RoleApiRecord): RoleRecord {
  const created = record.created_at || new Date().toISOString();
  const updated = record.updated_at || created;
  return {
    id: String(record.id ?? ""),
    name: record.name,
    guardName: record.guard_name ?? "web",
    companyId: record.company_id != null && record.company_id !== "" ? String(record.company_id) : null,
    companyName: record.company_name ?? null,
    isTemplate: Boolean(record.is_template),
    permissionsCount: typeof record.permissions_count === "number" ? record.permissions_count : 0,
    createdAt: created,
    updatedAt: updated,
  };
}

export function listRoles(): RoleRecord[] {
  return load().sort((a, b) => a.name.localeCompare(b.name));
}

const PER_PAGE = 50;

export async function syncRolesFromApi() {
  const collected: RoleApiRecord[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const response = await api.get("/api/v1/roles", {
      params: { page, per_page: PER_PAGE, guard_name: "web" },
    });
    const body = response.data;
    collected.push(...extractRowsFromResponse(body));
    lastPage = readLastPageFromListBody(body) ?? 1;
    page += 1;
  } while (page <= lastPage && page <= 50);

  const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
  save(records);
  return listRoles();
}

export async function fetchRole(id: ID): Promise<RoleDraft & { id: string; guardName: string }> {
  const response = await api.get<RoleShowApiResponse>(`/api/v1/roles/${id}`);
  const raw = response.data?.data;
  if (!raw || raw.id == null) throw new Error("Invalid role detail response.");
  const names = Array.isArray(raw.permissions) ? [...raw.permissions] : [];
  return {
    id: String(raw.id),
    name: raw.name,
    guardName: raw.guard_name ?? "web",
    permissionNames: names,
  };
}

export async function createRole(draft: RoleDraft): Promise<UpsertRoleResult> {
  const payload = {
    name: draft.name.trim(),
    guard_name: "web",
    permissions: draft.permissionNames,
  };
  const response = await api.post<UpsertRoleApiResponse>("/api/v1/roles", payload);
  const created = extractRoleFromUpsertBody(response.data);
  if (!created || created.id == null) throw new Error("Invalid create role response.");
  const record = mapApiRecord(created);

  const items = load();
  const idx = items.findIndex((d) => d.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);

  // Role permissions can affect current user's effective permissions.
  void refreshStoredAuthUser();

  return { record, message: response.data?.message || "Role created successfully." };
}

export async function updateRole(id: ID, draft: RoleDraft): Promise<UpsertRoleResult | null> {
  const items = load();
  const idx = items.findIndex((d) => d.id === id);
  if (idx === -1) return null;

  const payload = {
    name: draft.name.trim(),
    guard_name: "web",
    permissions: draft.permissionNames,
  };
  const response = await api.put<UpsertRoleApiResponse>(`/api/v1/roles/${id}`, payload);
  const updated = extractRoleFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update role response.");

  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);

  // Role permissions can affect current user's effective permissions.
  void refreshStoredAuthUser();

  return { record, message: response.data?.message || "Role updated successfully." };
}

export async function deleteRole(id: ID): Promise<DeleteRoleResult> {
  const response = await api.delete<DeleteRoleApiResponse>(`/api/v1/roles/${id}`);
  const items = load();
  const next = items.filter((d) => d.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  void refreshStoredAuthUser();
  return { success: removed, message: response.data?.message || "Role deleted successfully." };
}
